import { StrictLocalMsg } from "@faire/localization/react/StrictLocalMsg";
import { ServerSideLocalMsg } from "@faire/localization/rsc/ServerSideLocalMsg";
import type { InstanceOfComponent } from "@faire/slate/private/shared/types";
import { isValidElement } from "react";

/**
 * Builds an id from a prefix and potential ids. It will use the first non-undefined id, if provided.
 *
 * @param prefix - The prefix of the id.
 * @param ids - The ids to build the final id from.
 * @returns The final id.
 */
export const buildId = (
  prefix: string,
  ...ids: Array<
    | string
    | InstanceOfComponent<typeof StrictLocalMsg>
    | InstanceOfComponent<typeof ServerSideLocalMsg>
    | undefined
  >
) => {
  const finalId = ids.flatMap((id) => {
    const idStr = isLocalMsgElement(id) ? localMsgDefaultMessageProp(id) : id;
    if (typeof idStr !== "string") {
      return [];
    }
    // Collapse whitespace runs to a single hyphen (existing behavior), then
    // replace any remaining non-letter/number character ("&", "/", "%",
    // punctuation, etc.) one-to-one with an underscore so the generated id is
    // selector-safe. The one-to-one replacement (rather than stripping or
    // collapsing) preserves distinctness: "Bath & Body" -> "bath-_-body" stays
    // distinct from "Bath Body" -> "bath-body". Unicode letters and numbers are
    // preserved so non-Latin labels still produce a usable id. Without this, a
    // label like "Kitchen & Tabletop" produces an id containing "&", which
    // breaks <label htmlFor> association in some environments (e.g. jsdom/axe).
    const scrubbedId = idStr
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\p{L}\p{N}-]/gu, "_");
    if (!scrubbedId.length) {
      return [];
    }
    return [scrubbedId];
  })[0];
  if (!finalId) {
    return;
  }
  return `${prefix}-${finalId}`;
};

type LocalMsgElement = React.ReactElement<
  Pick<React.ComponentProps<typeof StrictLocalMsg>, "defaultMessage"> &
    Pick<React.ComponentProps<typeof ServerSideLocalMsg>, "defaultMessage">
>;

const isLocalMsgElement = (value: unknown): value is LocalMsgElement =>
  isValidElement(value) &&
  typeof (value?.props as unknown as LocalMsgElement["props"] | undefined)
    ?.defaultMessage === "string";

const localMsgDefaultMessageProp = (value: LocalMsgElement) =>
  value.props.defaultMessage;
